import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, UploadCloud, CheckCircle2, AlertTriangle, XCircle, FileText, Loader2 } from "lucide-react";

const CONFIG = {
  clients: {
    titre: "Importer les clients",
    accept: ".csv,text/csv",
    multiple: false,
    format: (
      <>
        <p className="mb-2">Déposez un fichier <b>CSV</b> avec une ligne d'en-tête. Colonnes reconnues (l'IA accepte des variantes) :</p>
        <code className="block text-[12px] bg-slate-50 border border-slate-200 rounded-lg p-2.5 leading-relaxed">
          prénom, nom, téléphone, email, adresse, date_naissance, secteur, tags, note
        </code>
        <ul className="mt-2 text-[12px] text-slate-500 list-disc pl-4 space-y-0.5">
          <li>Dates au format <b>JJ/MM/AAAA</b> ou <b>AAAA-MM-JJ</b>.</li>
          <li><b>tags</b> multiples séparés par <b>,</b> ou <b>/</b>. Secteurs et tags inexistants seront créés.</li>
        </ul>
      </>
    ),
    cols: [["prenom", "Prénom"], ["nom", "Nom"], ["telephone", "Téléphone"], ["email", "Email"], ["secteur", "Secteur"], ["tags", "Tags"], ["date_naissance", "Naissance"], ["note", "Note"]],
  },
  rdvs: {
    titre: "Importer les RDV",
    accept: ".csv,text/csv",
    multiple: false,
    format: (
      <>
        <p className="mb-2">Déposez un fichier <b>CSV</b> avec une ligne d'en-tête. Colonnes reconnues :</p>
        <code className="block text-[12px] bg-slate-50 border border-slate-200 rounded-lg p-2.5 leading-relaxed">
          patient, date, heure, durée, type_soin, secteur, statut
        </code>
        <ul className="mt-2 text-[12px] text-slate-500 list-disc pl-4 space-y-0.5">
          <li><b>patient</b> = nom complet ou email. Un patient inconnu sera <b>créé automatiquement</b>.</li>
          <li><b>heure</b> : 14h30 ou 14:30. Chaque RDV crée aussi sa <b>fiche de soin</b>.</li>
        </ul>
      </>
    ),
    cols: [["patient", "Patient"], ["date", "Date"], ["heure", "Heure"], ["duree_min", "Durée"], ["type_soin", "Type"], ["secteur", "Secteur"], ["statut", "Statut"]],
  },
  soins: {
    titre: "Importer des fiches de soins",
    accept: ".md,text/markdown",
    multiple: true,
    format: (
      <>
        <p className="mb-2">Déposez un ou plusieurs fichiers <b>.md</b>. Chaque fichier commence par un en-tête (front-matter) :</p>
        <code className="block text-[12px] bg-slate-50 border border-slate-200 rounded-lg p-2.5 whitespace-pre leading-relaxed">{`---
patient: Prénom Nom
date: 2026-06-15
titre: Lombalgie
---
(contenu markdown de la fiche…)`}</code>
        <ul className="mt-2 text-[12px] text-slate-500 list-disc pl-4 space-y-0.5">
          <li>Le <b>patient</b> est retrouvé par son nom (créé s'il n'existe pas).</li>
        </ul>
      </>
    ),
    cols: [["patient", "Patient"], ["date", "Date"], ["titre", "Titre"], ["fichier", "Fichier"]],
  },
};

const STATUS_UI = {
  ok: { icon: CheckCircle2, cls: "text-emerald-600", bg: "bg-emerald-50", label: "Prêt" },
  warn: { icon: AlertTriangle, cls: "text-amber-600", bg: "bg-amber-50", label: "À vérifier" },
  error: { icon: XCircle, cls: "text-red-600", bg: "bg-red-50", label: "À corriger" },
};

export default function ImportWizard({ kind, onClose, onDone }) {
  const cfg = CONFIG[kind];
  const [files, setFiles] = useState([]);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const analyser = async () => {
    if (!files.length) { toast.error("Ajoutez un fichier"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRows(data.rows);
      toast.success(`${data.count} ligne(s) analysée(s), ${data.ok} prête(s)`);
    } catch (e) {
      toast.error("Analyse impossible : " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const setCell = (i, field, value) => {
    setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, data: { ...r.data, [field]: value } } : r));
  };

  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const importer = async () => {
    const valides = rows.filter((r) => r.status !== "error");
    if (!valides.length) { toast.error("Aucune ligne importable"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", { kind, rows: valides });
      toast.success(`${data.created} enregistrement(s) importé(s)`);
      if (data.errors?.length) toast.warning(`${data.errors.length} ligne(s) en erreur`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error("Import impossible : " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const counts = rows ? { ok: rows.filter((r) => r.status === "ok").length, warn: rows.filter((r) => r.status === "warn").length, error: rows.filter((r) => r.status === "error").length } : null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="import-wizard">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white h-full w-full max-w-[760px] shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">{rows ? "Importation : valider les données" : "Importation"}</div>
            <h3 className="text-[18px] font-semibold" style={{ color: "var(--ink)" }}>{cfg.titre}</h3>
          </div>
          <button data-testid="import-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {!rows ? (
            <div className="space-y-4">
              <div className="text-[13px] text-slate-600 sx-card p-4 bg-white">{cfg.format}</div>
              <label className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-slate-400 transition-colors" data-testid="import-drop">
                <UploadCloud className="w-8 h-8 mx-auto text-slate-400" />
                <div className="mt-2 text-sm text-slate-600">{files.length ? `${files.length} fichier(s) sélectionné(s)` : "Cliquez pour choisir un fichier"}</div>
                <input type="file" className="hidden" accept={cfg.accept} multiple={cfg.multiple}
                  data-testid="import-file-input"
                  onChange={(e) => setFiles(Array.from(e.target.files || []))} />
              </label>
              {files.length > 0 && (
                <div className="space-y-1">
                  {files.map((f, i) => <div key={i} className="text-[12px] text-slate-500 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />{f.name}</div>)}
                </div>
              )}
            </div>
          ) : (
            <div>
              {counts && (
                <div className="flex gap-2 mb-3 text-[12px]">
                  <span className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-700">{counts.ok} prête(s)</span>
                  <span className="px-2 py-1 rounded-md bg-amber-50 text-amber-700">{counts.warn} à vérifier</span>
                  <span className="px-2 py-1 rounded-md bg-red-50 text-red-700">{counts.error} à corriger</span>
                </div>
              )}
              <div className="overflow-auto sx-card">
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="px-2 py-2 w-8"></th>
                      {cfg.cols.map(([k, l]) => <th key={k} className="px-2 py-2 font-medium">{l}</th>)}
                      <th className="px-2 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const su = STATUS_UI[r.status];
                      const Icon = su.icon;
                      return (
                        <tr key={i} className={`border-b border-slate-100 ${su.bg}`} data-testid={`import-row-${i}`} title={r.issues?.join(" · ")}>
                          <td className="px-2 py-1"><Icon className={`w-4 h-4 ${su.cls}`} /></td>
                          {cfg.cols.map(([k]) => (
                            <td key={k} className="px-1 py-1">
                              <input
                                data-testid={`import-cell-${i}-${k}`}
                                value={Array.isArray(r.data[k]) ? r.data[k].join(", ") : (r.data[k] ?? "")}
                                onChange={(e) => setCell(i, k, k === "tags" ? e.target.value.split(",").map((x) => x.trim()).filter(Boolean) : e.target.value)}
                                className="w-full min-w-[80px] px-1.5 py-1 rounded border border-transparent hover:border-slate-200 focus:border-slate-400 focus:bg-white bg-transparent outline-none"
                              />
                            </td>
                          ))}
                          <td className="px-2 py-1"><button onClick={() => removeRow(i)} className="text-slate-300 hover:text-red-500" data-testid={`import-del-${i}`}><X className="w-4 h-4" /></button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[12px] text-slate-400">Passez la souris sur l'icône d'état pour voir les remarques. Les lignes « à corriger » sont ignorées à l'import.</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
          {!rows ? (
            <button data-testid="import-analyser" onClick={analyser} disabled={busy || !files.length}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}Analyser
            </button>
          ) : (
            <>
              <button onClick={() => { setRows(null); setFiles([]); }} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">← Reprendre</button>
              <button data-testid="import-commit" onClick={importer} disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Importer {rows.filter((r) => r.status !== "error").length} ligne(s)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
